import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Save, Car, AlertTriangle, ExternalLink } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;
const digits = (v) => String(v || "").replace(/\D/g, "");

const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const emptyVehicle = () => ({ _key: newKey(), plate: "", make_model: "", paint_code: "" });
const B2B = { is_business: false, company: "", uen: "", billing_address: "", contact_person: "", payment_terms_days: 30, email: "", billing_cycle: "per_job", message_language: "zh" };
const emptyForm = () => ({ name: "", phone: "", notes: "", vehicles: [emptyVehicle()], ...B2B });

const formFromCustomer = (c) => ({
  name: c.name,
  phone: c.phone,
  notes: c.notes || "",
  email: c.email || "", billing_cycle: c.billing_cycle || "per_job", message_language: c.message_language || "zh", is_business: !!c.is_business, company: c.company || "", uen: c.uen || "", billing_address: c.billing_address || "", contact_person: c.contact_person || "", payment_terms_days: c.payment_terms_days ?? 30,
  vehicles: c.vehicles.length ? c.vehicles.map((v) => ({ ...v, _key: v.id })) : [emptyVehicle()],
});

function Field({ label, zh, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label} <span className="font-zh normal-case text-amber-300/70">{zh}</span>{required && " *"}</Label>
      {children}
    </div>
  );
}

function VehicleRow({ v, index, canRemove, onChange, onRemove }) {
  const cls = "bg-slate-950/60 border-slate-700 h-9 text-sm";
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3 grid grid-cols-2 sm:grid-cols-[110px_1fr_110px_auto] gap-2 items-end" data-testid={`vehicle-row-${index}`}>
      <div className="space-y-1 min-w-0">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500 whitespace-nowrap">Plate 车牌 *</Label>
        <Input value={v.plate} onChange={(e) => onChange({ plate: e.target.value })} data-testid={`vehicle-plate-${index}`} className={`${cls} font-mono-num uppercase`} placeholder="ABC123" />
      </div>
      <div className="space-y-1 min-w-0">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500 whitespace-nowrap">Make & Model 车型</Label>
        <Input value={v.make_model} onChange={(e) => onChange({ make_model: e.target.value })} data-testid={`vehicle-model-${index}`} className={cls} placeholder="Toyota Camry 2019" />
      </div>
      <div className="space-y-1 min-w-0">
        <Label className="text-[10px] uppercase tracking-wider text-slate-500 whitespace-nowrap">Paint Code 色号</Label>
        <Input value={v.paint_code} onChange={(e) => onChange({ paint_code: e.target.value })} data-testid={`vehicle-paint-${index}`} className={`${cls} font-mono-num uppercase`} placeholder="1G3" />
      </div>
      <button type="button" onClick={onRemove} disabled={!canRemove} data-testid={`vehicle-remove-${index}`} className="h-9 w-9 grid place-items-center justify-self-end text-slate-500 hover:text-rose-400 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
}

// Form state, vehicle editing, duplicate detection and submit. Owned by CustomerDialog only.
function useCustomerForm({ open, onOpenChange, customer, onSave }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(customer ? formFromCustomer(customer) : emptyForm());
  }, [open, customer]);

  const setVehicle = (key, patch) => setForm((f) => ({ ...f, vehicles: f.vehicles.map((v) => (v._key === key ? { ...v, ...patch } : v)) }));
  const addVehicle = () => setForm((f) => ({ ...f, vehicles: [...f.vehicles, emptyVehicle()] }));
  const removeVehicle = (key) => setForm((f) => ({ ...f, vehicles: f.vehicles.filter((v) => v._key !== key) }));

  // Duplicate check: same contact number (or plate) already on file → warn live, and double-confirm on save
  const [dupes, setDupes] = useState([]);
  const [confirmDupes, setConfirmDupes] = useState(false);
  const findDupes = async (phone, vehicles) => {
    if (!digits(phone) && !vehicles.length) return [];
    try {
      const { data } = await axios.get(`${API}/customers/duplicates`, { params: { phone, plates: vehicles.map((v) => v.plate).join(","), exclude: customer?.id || undefined } });
      return data;
    } catch { return []; }
  };
  useEffect(() => {
    if (!open) { setDupes([]); setConfirmDupes(false); return; }
    if (digits(form.phone).length < 6) { setDupes([]); return; }
    const t = setTimeout(() => findDupes(form.phone, []).then(setDupes), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.phone, open]);

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) return toast.error("Name and contact number are required");
    const vehicles = form.vehicles
      .filter((v) => v.plate.trim())
      .map(({ _key, ...v }) => ({ ...v, plate: v.plate.trim().toUpperCase() }));
    if (vehicles.length === 0) return toast.error("Add at least one car plate");
    if (!confirmDupes) {
      const found = await findDupes(form.phone, vehicles);
      if (found.length) { setDupes(found); setConfirmDupes(true); return; } // show the confirm panel; second tap creates anyway
    }
    setSaving(true);
    try {
      await onSave({ ...form, name: form.name.trim(), phone: form.phone.trim(), vehicles });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return { form, setForm, saving, setVehicle, addVehicle, removeVehicle, dupes, confirmDupes, submit };
}

function VehiclesSection({ form, addVehicle, setVehicle, removeVehicle }) {
  return (
    <div className="space-y-2 mt-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> Vehicles <span className="font-zh normal-case text-amber-300/70">车辆</span></Label>
        <Button type="button" variant="ghost" size="sm" onClick={addVehicle} data-testid="add-vehicle-btn" className="text-amber-300 hover:text-amber-200 hover:bg-amber-500/10 h-7 text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Add vehicle</Button>
      </div>
      {form.vehicles.map((v, i) => (
        <VehicleRow key={v._key} v={v} index={i} canRemove={form.vehicles.length > 1}
          onChange={(patch) => setVehicle(v._key, patch)} onRemove={() => removeVehicle(v._key)} />
      ))}
    </div>
  );
}

function BusinessSection({ form, setForm }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3 space-y-3" data-testid="customer-b2b-block">
      <label className="flex items-center gap-3 cursor-pointer">
        <input type="checkbox" checked={form.is_business} onChange={(e) => setForm({ ...form, is_business: e.target.checked })} data-testid="customer-business-toggle" className="w-4 h-4 accent-sky-400" />
        <span className="font-semibold text-sm">Business customer (B2B) <span className="font-zh text-xs text-sky-300/80">企业客户</span></span>
        <span className="text-xs text-slate-500 ml-auto">fleet, dealer, workshop</span>
      </label>
      {form.is_business && (
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Company" zh="公司名称"><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="customer-company-field" className="bg-slate-950/60 border-slate-700" placeholder="Tan Motors Pte Ltd" /></Field>
          <Field label="UEN / Reg. no." zh="注册号"><Input value={form.uen} onChange={(e) => setForm({ ...form, uen: e.target.value })} data-testid="customer-uen-field" className="bg-slate-950/60 border-slate-700 font-mono-num uppercase" placeholder="201912345A" /></Field>
          <Field label="Contact person" zh="联系人"><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="customer-contact-field" className="bg-slate-950/60 border-slate-700" placeholder="Mr Tan (Fleet manager)" /></Field>
          <Field label="Email (for invoices)" zh="电邮"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="customer-email-field" className="bg-slate-950/60 border-slate-700" placeholder="accounts@company.com.sg" /></Field>
          <Field label="Payment terms (days)" zh="付款期限"><Input type="number" min="0" value={form.payment_terms_days} onChange={(e) => setForm({ ...form, payment_terms_days: Number(e.target.value) })} data-testid="customer-terms-field" className="bg-slate-950/60 border-slate-700 font-mono-num" /></Field>
          <Field label="Billing" zh="开票方式">
            <div className="flex rounded-md border border-slate-700 bg-slate-950/60 p-0.5" data-testid="customer-billing-cycle">
              {[["per_job", "Per job"], ["monthly", "Monthly invoice"]].map(([k, l]) => (
                <button key={k} type="button" data-on={form.billing_cycle === k} onClick={() => setForm({ ...form, billing_cycle: k })} data-testid={`customer-billing-${k}`}
                  className="flex-1 h-8 rounded text-xs font-semibold text-slate-400 data-[on=true]:bg-sky-500 data-[on=true]:text-slate-950 transition-colors">{l}</button>
              ))}
            </div>
          </Field>
          <div className="sm:col-span-2"><Field label="Billing address" zh="账单地址"><Input value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} data-testid="customer-billing-field" className="bg-slate-950/60 border-slate-700" placeholder="12 Sin Ming Dr, #01-23, Singapore 575712" /></Field></div>
        </div>
      )}
    </div>
  );
}

function LanguageAndNotes({ form, setForm }) {
  return (
    <>
      <Field label="Message language" zh="讯息语言">
        <div className="flex rounded-md border border-slate-700 bg-slate-950/60 p-0.5" data-testid="customer-message-language">
          {[["zh", "English + 中文"], ["ta", "English + தமிழ்"], ["ms", "English + Melayu"]].map(([k, l]) => (
            <button key={k} type="button" data-on={form.message_language === k} onClick={() => setForm({ ...form, message_language: k })} data-testid={`customer-language-${k}`}
              className="flex-1 h-8 rounded text-xs font-semibold text-slate-400 data-[on=true]:bg-violet-500 data-[on=true]:text-white transition-colors">{l}</button>
          ))}
        </div>
      </Field>

      <Field label="Notes" zh="备注">
        <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="customer-notes-field" className="bg-slate-950/60 border-slate-700" placeholder="Prefers WhatsApp, referred by…" />
      </Field>
    </>
  );
}

function DuplicateWarning({ dupes, confirmDupes, onOpenChange, onOpenExisting }) {
  if (!dupes.length) return null;
  return (
    <div className={`rounded-xl border p-3 space-y-2 ${confirmDupes ? "border-rose-500/60 bg-rose-500/10" : "border-amber-500/40 bg-amber-500/10"}`} data-testid="customer-duplicate-warning">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <AlertTriangle className={`w-4 h-4 ${confirmDupes ? "text-rose-300" : "text-amber-300"}`} />
        {confirmDupes ? "Possible duplicate — create anyway?" : "This contact number is already on file"} <span className="font-zh text-xs opacity-70">{confirmDupes ? "疑似重复，仍要新建？" : "号码已存在"}</span>
      </div>
      <ul className="space-y-1">
        {dupes.slice(0, 4).map((c) => (
          <li key={c.id} className="flex items-center justify-between gap-2 text-xs text-slate-200" data-testid={`customer-duplicate-${c.id}`}>
            <span className="truncate"><span className="font-semibold">{c.name}</span> · <span className="font-mono-num">{c.phone}</span>{c.vehicles?.length ? ` · ${c.vehicles.map((v) => v.plate).join(", ")}` : ""}{c.is_business ? " · B2B" : ""}</span>
            {onOpenExisting && <button type="button" onClick={() => { onOpenChange(false); onOpenExisting(c); }} data-testid={`customer-duplicate-open-${c.id}`} className="shrink-0 inline-flex items-center gap-1 text-amber-300 hover:underline">Open existing <ExternalLink className="w-3 h-3" /></button>}
          </li>
        ))}
      </ul>
      {!confirmDupes && <p className="text-[11px] text-slate-400">Saving will ask you to confirm. Prefer adding a new plate to the existing customer instead.</p>}
    </div>
  );
}

export default function CustomerDialog({ open, onOpenChange, customer, onSave, onOpenExisting }) {
  const { form, setForm, saving, setVehicle, addVehicle, removeVehicle, dupes, confirmDupes, submit } = useCustomerForm({ open, onOpenChange, customer, onSave });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-[#111827] border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-extrabold">{customer ? "Edit Customer" : "New Customer"} <span className="font-zh text-amber-300/80 text-lg">{customer ? "编辑客户" : "新客户"}</span></DialogTitle>
          <DialogDescription className="text-slate-400">Contact details and vehicles for retention follow-ups.</DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Client Name" zh="姓名" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="customer-name-field" className="bg-slate-950/60 border-slate-700" placeholder="e.g. David Chen" />
          </Field>
          <Field label="Contact Number" zh="电话" required>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="customer-phone-field" className="bg-slate-950/60 border-slate-700 font-mono-num" placeholder="04xx xxx xxx" inputMode="tel" />
          </Field>
        </div>

        <VehiclesSection form={form} addVehicle={addVehicle} setVehicle={setVehicle} removeVehicle={removeVehicle} />
        <BusinessSection form={form} setForm={setForm} />
        <LanguageAndNotes form={form} setForm={setForm} />
        <DuplicateWarning dupes={dupes} confirmDupes={confirmDupes} onOpenChange={onOpenChange} onOpenExisting={onOpenExisting} />

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="customer-save-btn" className={`font-bold ${confirmDupes ? "bg-rose-600 hover:bg-rose-500 text-white" : "bg-amber-500 hover:bg-amber-400 text-slate-950"}`}><Save className="w-4 h-4 mr-2" /> {confirmDupes ? "Yes, create anyway · 确认新建" : customer ? "Save changes" : "Add customer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
